/**
 * SetPasswordScreen — "choose your own password" before anything else.
 *
 * ADDED 2026-10-29 (Lis, follow-up (a)). A person a super admin added, or whose password a
 * super admin reset, signs in with a temporary password. Until they choose their own, every
 * signed-in page shows this instead (App.tsx ProtectedRoute). The temporary password is the
 * "current" one; the new one must be at least 8 characters and different from it (the server
 * refuses the same one too).
 */

import React, { useState } from 'react';
import { KeyRound } from 'lucide-react';
import { changeOwnPassword } from '../../lib/authNative';

interface Props {
  /** Called once the new password is saved. */
  onDone: () => void;
  /** Lets the person leave without changing it (they are asked again next time). */
  onSignOut: () => void;
}

export default function SetPasswordScreen({ onDone, onSignOut }: Props) {
  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [again, setAgain] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    if (!current) return setError('Enter the temporary password you were given.');
    if (next.length < 8) return setError('Your new password needs at least 8 characters.');
    if (next !== again) return setError('The two new passwords are not the same.');
    if (next === current) return setError('Choose a password different from the temporary one.');
    setSaving(true);
    try {
      const res = await changeOwnPassword(current, next);
      if (res.error) setError(res.error);
      else onDone();
    } finally {
      setSaving(false);
    }
  };

  const field = 'mt-1 w-full text-sm border border-white/10 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-teal-500';

  return (
    <div className="min-h-screen flex items-center justify-center p-4">
      <form onSubmit={e => void submit(e)} noValidate className="card w-full max-w-sm flex flex-col gap-3">
        <div className="flex items-center gap-2 text-white">
          <KeyRound size={18} />
          <h1 className="text-lg font-semibold">Choose your password</h1>
        </div>
        <p className="text-sm text-white/60">
          You signed in with a temporary password. Choose your own to continue.
        </p>
        <label className="text-sm text-white/70">
          Temporary password
          <input type="password" autoComplete="current-password" value={current}
            onChange={e => setCurrent(e.target.value)} className={field} />
        </label>
        <label className="text-sm text-white/70">
          New password
          <input type="password" autoComplete="new-password" value={next}
            onChange={e => setNext(e.target.value)} className={field} />
        </label>
        <label className="text-sm text-white/70">
          New password again
          <input type="password" autoComplete="new-password" value={again}
            onChange={e => setAgain(e.target.value)} className={field} />
        </label>
        {error && <p role="alert" className="text-sm text-red-400">{error}</p>}
        <button type="submit" disabled={saving} className="btn-primary">
          {saving ? 'Saving…' : 'Save and continue'}
        </button>
        <button type="button" onClick={onSignOut} className="text-xs text-white/40 hover:text-white/70">
          Sign out
        </button>
      </form>
    </div>
  );
}
